/** Subscriptions hooks. API data only when authenticated (errors → isError + empty). */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { SUBSCRIPTIONS_QUERY_PREFIX, queryKeys } from "@/lib/query-client";
import type { Subscription } from "@/types";
import { useAuthStore } from "@/features/auth/auth.store";
import { subscriptionsApi } from "./subscriptions.api";
import {
  toSubscription,
  toSubscriptionWriteBody,
  type SubscriptionInput,
} from "./subscriptions.adapter";

const LIST_PARAMS = { limit: 100, sortBy: "createdAt", sortOrder: "desc" as const };

export type DataSource = "api" | "mock";

export interface UseSubscriptionsResult {
  subscriptions: Subscription[];
  isLoading: boolean;
  isError: boolean;
  isFetching: boolean;
  source: DataSource;
  error: unknown;
  refetch: () => void;
}

export function useSubscriptions(): UseSubscriptionsResult {
  const status = useAuthStore((s) => s.status);
  const authed = status === "authenticated";

  const query = useQuery({
    queryKey: queryKeys.subscriptionsList(LIST_PARAMS),
    queryFn: () => subscriptionsApi.list(LIST_PARAMS),
    enabled: authed,
  });

  if (query.data)
    return {
      subscriptions: query.data.data.map(toSubscription),
      isLoading: false,
      isError: false,
      isFetching: query.isFetching,
      source: "api",
      error: null,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      subscriptions: [],
      isLoading: false,
      isError: true,
      isFetching: false,
      source: "api",
      error: query.error,
      refetch: query.refetch,
    };
  return {
    subscriptions: [],
    isLoading: query.isLoading,
    isError: false,
    isFetching: query.isFetching,
    source: "api",
    error: null,
    refetch: query.refetch,
  };
}

function useInvalidateSubscriptions() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: SUBSCRIPTIONS_QUERY_PREFIX });
}

export function useCreateSubscription() {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (input: SubscriptionInput) => {
      return subscriptionsApi.create(toSubscriptionWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useUpdateSubscription() {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: SubscriptionInput }) => {
      return subscriptionsApi.update(id, toSubscriptionWriteBody(input));
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

export function useArchiveSubscription() {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (id: string) => {
      return subscriptionsApi.archive(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}

const ARCHIVED_PARAMS = {
  archived: true,
  limit: 100,
  sortBy: "createdAt",
  sortOrder: "desc" as const,
};

/** D3: posts a Movement OUT on the paying account and advances the renewal date. */
export function useRecordSubscriptionPayment() {
  const invalidate = useInvalidateSubscriptions();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, accountId }: { id: string; accountId?: string }) => {
      return subscriptionsApi.recordPayment(id, accountId ? { accountId } : {});
    },
    onSuccess: () => {
      void invalidate();
      void qc.invalidateQueries({ queryKey: ["treasury"] });
      // Le Movement posté est relu par la compta et le dashboard (ADR-001).
      void qc.invalidateQueries({ queryKey: ["accounting"] });
      void qc.invalidateQueries({ queryKey: ["reports"] });
    },
  });
}

export function useArchivedSubscriptions() {
  const authed = useAuthStore((s) => s.status) === "authenticated";
  const query = useQuery({
    queryKey: queryKeys.subscriptionsList(ARCHIVED_PARAMS),
    queryFn: () => subscriptionsApi.list(ARCHIVED_PARAMS),
    enabled: authed,
  });
  if (query.data)
    return {
      subscriptions: query.data.data.map(toSubscription),
      isLoading: false,
      isError: false,
      refetch: query.refetch,
    };
  if (query.isError)
    return {
      subscriptions: [] as Subscription[],
      isLoading: false,
      isError: true,
      refetch: query.refetch,
    };
  return {
    subscriptions: [] as Subscription[],
    isLoading: query.isLoading,
    isError: false,
    refetch: query.refetch,
  };
}

export function useRestoreSubscription() {
  const invalidate = useInvalidateSubscriptions();
  return useMutation({
    mutationFn: async (id: string) => {
      return subscriptionsApi.restore(id);
    },
    onSuccess: () => {
      void invalidate();
    },
  });
}
